// eslint-disable-next-line no-unused-vars
import React, { useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import { bool, shape, string, arrayOf, number, func, object, oneOfType, array } from 'prop-types';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Loading,
  Search,
  SkeletonPlaceholder,
  SkeletonText,
  ComposedModal,
  ModalHeader,
  ModalBody
} from '@carbon/react';
import { Info, MapPinHouse, Mail, Pencil, Trash, Check } from 'lucide-react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { uniqueId, noop } from 'lodash';

import { NWComboBox, NWTextInput, NWCheckbox, NWNotification } from 'Components/shared';

const StyledAddressesComponent = styled.div`
  .viewBtn {
    height: 32px;
    width: 32px;
    background-color: #fcfdff;
    border: 1px solid #dde6f2;
    
    svg {
      stroke: #3a3a3a;
    }

    &:hover {
      background-color: #d0ddef;
    }
  }

  .errorWrapper {
    margin: 0 15px;
  }

  .cds--modal-content {
    margin-bottom: 1rem;

    p {
      padding-right: 0;
    }
  }

  .modalActions {
    margin-top: 20px;
    display: flex;
    justify-content: space-between;
  }
`;

const StyledTextInputSkeleton = styled.div`
  .cds--label {
    &:before {
      background: transparent;
      transform: none;
      transition: none;
      animation: none;
      will-change: unset;
    }

    &:after {
      content: '*';
      display: inline-block;
      margin-left: 5px;
      color: ${ props => props.theme.colors.default };
    }

    background: transparent;
    display: inline-block;
    font-size: 0.875rem;
    line-height: 1rem;
    color: ${ props => props.theme.colors.darkGray };
    width: auto;
    margin: 0 !important;
  }

  margin: 1px 0 15px;
`;

const StyledButton = styled(Button)`
  width: 200px;
  height: 40px;
  padding: 0;
  justify-content: center;

  &.primary {
    border: 1px solid #1f59a2;
    color: #1f59a2;
  }

  &.danger {
    background-color: #da1e28;
    border: 1px solid transparent;
    color: #fff;
  }

  &.tab {
    color: #1f59a2;
  }
`;

const StyledActions = styled.div`
  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 0;
  }

  .form {
    border: 1px solid #e2e2e2;
    margin-bottom: 20px;

    .defaultDelivery .cds--form-requirement {
      background: #DDE6F2;
      max-height: 30px;
      padding: 5px;
      width: 100%;
      border-radius: 5px;
      color: #3A3A3A;
    }

    .headerTitle {
      display: flex;
      align-items: center;
      height: 48px;
      font-size: 1.25rem;
      color: #3A3A3A;
      background-color: #F5F7F9;
      text-align: left;
      padding: 0 14px;
    }

    .content {
      padding: 1rem;
      width: 100%;
      display: grid;
      gap: 2.25rem;
      grid-template-columns: repeat(2, minmax(0px, 1fr));

      .recipientEmailField {
        display: flex;
        align-items: center;
        gap: 10px;
      }

      .smallFields {
        width: 100%;
        display: grid;
        gap: 2.25rem;
        grid-template-columns: repeat(2, minmax(0px, 1fr));
        
        > div {
          margin: 0;
        }
      }
    }

    .footer {
      display: flex;
      padding: 0 20px 20px;
      gap: 20px;
    }
  }

  @media screen and (max-width: 1024px) {
    .header {
      h1 {
        font-size: 24px;
        font-weight: 600;
      }

      .primary {
        font-size: 16px;
        border: none;
        width: auto;
        height: auto;

        span {
          font-size: 24px;
          padding-right: 5px;
        }

        &:hover {
          background-color: transparent;
        }
      }
    }

    .form {
      .content {
        grid-template-columns: auto;
        gap: 0;
        padding: 10px;

        .smallFields {
          grid-template-columns: auto;
          gap: 0;
        }

        .deliveryLabel {
          display: inline-block;
          margin-top: 15px;
        }
      }

      .footer {
        padding: 0 10px 20px;
        gap: 10px;

        > button {
          width: auto;
          flex: 1;
        }
      }
    }
  }
`;

const StyledAddressList = styled.div`
  .header {
    display: flex;
    justify-content: flex-end;
    background: linear-gradient(90deg, #040F2A 0%, #041B54 100%);
    min-height: 3rem;
    margin-bottom: 15px;
    padding: 0 20px;

    .tabs {
      padding-top: calc(3rem - 40px);
    }

    .search-container {
      position: relative;
      margin: 4px 0;

      .search {
        display: flex;
        justify-content: center;
        max-width: 18rem;

        .cds--search-close {
          right: 40px;
          width: 30px;

          &:hover {
            background-color: transparent;
            outline: none;
            border: none;
          }

          &:before {
            content: none;
          }
        }

        .cds--search-magnifier {
          position: absolute;
          right: 12px;
          width: 20px;
          height: 20px;

          svg {
            position: absolute;
            z-index: 2;
            top: 50%;
            left: auto;
            width: 1rem;
            height: 1rem;
            stroke: #525252;
            pointer-events: none;
            transform: translateY(-50%);
          }
        }

        input {
          border-radius: 5px;
          padding: 0 70px 0 1rem;
          width: 256px;
          height: 40px;
          margin: 10px 0;
        }
      }

      .searchButton {
        position: absolute;
        right: 0;
        top: 0;
        padding: 0;
        height: 100%;
        width: 45px;
        opacity: 0;
        background-color: transparent;
        cursor: pointer;
        z-index: 99;
      }
    }
  }

  .addresses-list {
    display: flex;
    flex-wrap: wrap;
  }

  .noResults {
    text-align: center;
    width: 100%;
    padding: 30px;
  }

  .addresses {
    flex-basis: calc(50% - 1rem);
    box-sizing: border-box;

    &:nth-of-type(even) {
      margin-left: 2rem;
    }
  }

  .address-container {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    background: #f5f5f5 0 0 no-repeat padding-box;
    border-radius: 0.375rem;
    margin-bottom: 2rem;
    min-height: 9rem;
    padding: 1rem;
    height: calc(100% - 20px);

    .address-right {
      flex-grow: 1;

      .address-street {
        word-break: break-all;
      }

      .reception-contact {
        margin-top: 14px;
        font-size: .875rem;
        line-height: 1.5rem;
        overflow-wrap: anywhere;

        .reception-contact-label {
          color: #707070;
        }

        .reception-contact-details {
          color: #3a3a3a;
          font-weight: 600;
        }
      }
    }

    .reason {
      display: flex;
      align-items: baseline;

      span {
        margin-left: 10px;
      }

      .reason-message {
        font-size: .875rem;
        margin-top: 12px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 200px;
      }
    }
  }

  .address-icon-background {
    background: #fff 0 0 no-repeat padding-box;
    border-radius: 5rem;
    height: 5rem;
    padding: 1rem;
    position: relative;
    vertical-align: middle;
    width: 5rem;
  }

  .address-icon {
    left: 25%;
    position: absolute;
    top: 30%;

    svg {
      stroke: #223F64;
    }
  }

  .address-data {
    line-height: 1.5rem;
    padding: 0 2rem 0;
    max-width: 500px;
    overflow-wrap: break-word;
  }

  .address-date {
    line-height: 1.5rem;
     
    span {
      margin-right: 5px;
      color: #707070;
    }
  }

  .address-actions {
    margin: 0.75rem 0 0 0.75rem;
    display: flex;
    justify-content: space-between;

    button {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 30px;
      width: 30px;
      border: none;
      background: transparent;
      cursor: pointer;

      &.edit {
        margin-right: 10px;
        background-color: #fcfdff;
        border: 1px solid #dde6f2;

        &:hover {
          background-color: #d0ddef;
        }
      }

      &.delete {
        background-color: #fcf3f1;
        border: 1px solid #fee7e5;

          .cds--loading {
            margin-left: 20px;
            svg {
              height: 16px;
            }
          }

        &:hover {
          background-color: #fbb2ac;
        }
      }

      span {
        display: none;
      }
    }
  }

  .address-default-delivery {
    align-items: center;
    background-color: #498246;
    border-radius: 1rem;
    display: flex;
    height: 2rem;
    justify-content: center;
    margin-left: 2.65rem;
    margin-top: 1.8rem;
    position: absolute;
    width: 2rem;

    svg {
      stroke: #fff;
    }
  }

  .address-badges {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .address-default {
    border: 1px solid #d52b1e;
    color: #d52b1e;
    display: inline-block;
    border-radius: 0.375rem;
    font-size: .875rem;
    line-height: 1.25rem;
    margin: 10px 0 0 2rem;
    padding: 0 1rem;
    background: #fff;

    &.billing {
      border: 1px solid #e8720b;
      color: #e8720b;
    }
  }

  @media screen and (max-width: 1024px) {
    .header {
      background: #fff;
      padding: 0;

      .search-container {
        width: 100%;

        .search {
          max-width: 100%;

          .cds--search-input {
            width: 100%;
            background-color: #fff;
            border: 1px solid #e2e2e2;
          }
        }
      }
    }

    .addresses-list {
      flex-direction: column;

      .addresses {
        margin-left: 0 !important;

        .address-container {
          margin-bottom: 15px;
          flex-wrap: wrap;

          .address-right {
            flex-basis: 0;

            .address-data {
              padding: 0 0 0 1.2rem;
            }

            .address-badges {
              flex-direction: column;
              align-items: start;
              gap: 0;

              .address-default {
                margin-left: 1rem;
              }
            }
          }

          .address-actions {
            flex: 1;
            flex-basis: 100%;
            margin-left: 0;

            button {
              flex: 1;

              span {
                display: inline;
                margin-left: 5px;
              }
            }
          }
        }
      }
    }
  }
`;

const StyledSecurityModal = styled(ComposedModal)`

  .cds--modal-close {
    display: none;
  }

  .cds--modal-content {
    padding: 0;
    margin: 0;
  }

  .modalIcon {
    display: flex;
    justify-content: center;

    .iconWrapper {
      background: #f3f2f2;
      border: 1px solid #e2e2e2;
      margin: 24px 0 32px;
      width: 130px;
      height: 130px;
      border-radius: 65px;
      display: flex;
      align-items: center;
      justify-content: center;

      svg {
        width: 60px;
        height: 60px;
        stroke: #3a3a3a;
      }
    }
  }

  .body {
    text-align: center;
    margin-bottom: 40px;

    .message {
      padding: 0 24px;

      p {
        font-size: 30px;
        line-height: 36px;
        margin: 12px 0;
        
        span {
          font-weight: 600;
        }
      }
    }

    .infoMessage {
      span {
        color: #999999;
      }

      svg {
        margin-left: 10px;
      }

      .react-tooltip {
        max-width: 300px;
      }
    }
  }

  .modalError {
    padding: 0 80px;
  }

  .footer {
    background: #F3F2F2;
    padding: 32px 24px;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    flex-direction: column;

    .validationCode {
      min-width: 320px;
      margin-bottom: 0;

      .cds--form-item {
        align-items: center;
      }
    }

    .skip {
      margin-top: 16px;
    }

    .danger, .skip {
      min-width: 320px;
    }
  }
`;

const StyledSkeletonContainer = styled.div`
  .icon-placeholder {
    margin: 0 2rem 0 0;
    width: 120px;
    height: 90px;
  }

  div {
    width: 100%;
  }
`;

const fields = {
  firstName: 'firstName',
  lastName: 'lastName',
  phone: 'phone',
  recipientEmail: 'recipientEmail',
  county: 'county',
  city: 'city',
  street: 'street',
  number: 'number',
  building: 'building',
  floor: 'floor',
  apartment: 'apartment',
  postalCode: 'postalCode',
  defaultDelivery: 'defaultDelivery',
  validationCode: 'validationCode',
};

const AddressesComponent = ({
  addresses,
  addressesLoading,
  isActionsOpen,
  selectedCounty,
  counties,
  handleSelectedCounty,
  selectedCity,
  cities,
  loadingCities,
  handleSubmit,
  isRequestPending,
  postError,
  handleSearchAddress,
  handleDeleteAddress,
  handleEditAddress,
  handleAddAddress,
  search,
  handleSearchChange,
  loadingDelete,
  edit,
  isExternalReseller,
  shouldShowDefaultDeliveryWarning,
  errors,
  email,
  modalOpen,
  validationCodeValue,
  validationCodePending,
  validators,
  register,
  rhfErrors,
  setValue,
  control,
  recipientEmail,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const actionsRef = useRef(null);

  useEffect(() => {
    if (errors.length) {
      actionsRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [errors]);

  const renderReason = reason => {
    const uid = uniqueId();

    return (
      <div>
        <span id={ `stock-tooltip-${ uid }}` }><Info size={ 18 } /></span>
        <ReactTooltip
          className="stockTooltip"
          anchorId={ `stock-tooltip-${ uid }}` }
          variant="light"
          offset={ 10 }
          content={ (
            <p>{ reason }</p>
          ) }
        />
      </div>
    );
  };

  const sharedProps = fieldName => ({
    id: fieldName,
    name: fieldName,
    invalid: !!rhfErrors[fieldName]?.message.length,
    invalidText: rhfErrors[fieldName]?.message,
    ...register(fieldName, { ...validators[fieldName] }),
  });

  const sharedComboBoxProps = fieldName => ({
    id: fieldName,
    name: fieldName,
    invalid: rhfErrors[fieldName]?.message.length,
    invalidText: rhfErrors[fieldName]?.message,
    control,
    rules: { ...validators[fieldName] }
  });

  return (
    <StyledAddressesComponent theme={ theme }>
      { errors && <NWNotification errors={ errors } delay={ 10 } /> }
      <StyledActions>
        <StyledSecurityModal
          preventCloseOnClickOutside
          open={ modalOpen }
          size="md"
        >
          <ModalHeader>
            <h4>{ t('pages.addAddress') }</h4>
          </ModalHeader>
          <ModalBody>
            <div className="modalIcon">
              <div className="iconWrapper">
                <Mail />
              </div>
            </div>
            <div className="body">
              <div className="message">
                <p dangerouslySetInnerHTML={ { __html: t('pages.verificationEmailText', { email }) } } />
              </div>
              <div className="infoMessage">
                <span id="stock-tooltip-email">{ t('pages.noEmailAccess') }<Info size={ 18 } /></span>
                <ReactTooltip
                  className="stockTooltip"
                  anchorId="stock-tooltip-email"
                  variant="light"
                  offset={ 10 }
                  content={ (
                    <p>{ t('pages.securityTooltip') }</p>
                  ) }
                />
              </div>
            </div>
            { postError && (
              <div className="modalError">
                <NWNotification errors={ postError } />
              </div>
            ) }
            <div className="footer">
              <NWTextInput
                className="validationCode"
                light
                required
                labelText={ t('pages.enterEmailCode') }
                { ...sharedProps(fields.validationCode) }
              />
              <StyledButton
                onClick={ () => handleSubmit({ isNewAddress: true, withValidationCode: true }) }
                className="danger"
                disabled={ isRequestPending || !validationCodeValue?.length || rhfErrors.validationCode?.message.length }
              >
                { isRequestPending && <Loading active={ isRequestPending } small withOverlay={ false } /> }
                { t('pages.confirmCode') }
              </StyledButton>
              <StyledButton
                className="primary skip"
                kind="ghost"
                onClick={ () => handleSubmit({ isNewAddress: true, withValidationCode: false }) }
              >
                { t('pages.skipThisStep') }
              </StyledButton>
            </div>
          </ModalBody>
        </StyledSecurityModal>
        <div ref={ actionsRef } className="header">
          <h1>{ t('pages.addressesTitle') }</h1>
          { !isExternalReseller && (
            <StyledButton
              className="primary"
              kind="ghost"
              onClick={ () => handleAddAddress(!isActionsOpen) }
              data-test="addAddress"
            >
              <span>+</span>
              { t('pages.addAddress') }
            </StyledButton>
          ) }
        </div>
        { isActionsOpen && (
          <form className="form">
            <div className="headerTitle">
              <h4>{ edit ? t('pages.editAddress') : t('pages.addAddress') }</h4>
            </div>
            <div className="content">
              <section>
                <NWTextInput
                  light
                  required
                  readOnly={ !!edit }
                  labelText={ t(`pages.requestForm.${ fields.firstName }`) }
                  { ...sharedProps(fields.firstName) }
                />
                <NWTextInput
                  light
                  required
                  readOnly={ !!edit }
                  labelText={ t(`pages.requestForm.${ fields.lastName }`) }
                  { ...sharedProps(fields.lastName) }
                />
                <NWTextInput
                  light
                  required
                  readOnly={ !!edit }
                  labelText={ t(`pages.requestForm.${ fields.phone }`) }
                  { ...sharedProps(fields.phone) }
                />
                <div className="recipientEmailField">
                  <NWTextInput
                    light
                    required
                    readOnly={ !!edit }
                    labelText={ t(`pages.requestForm.${ fields.recipientEmail }`) }
                    { ...sharedProps(fields.recipientEmail) }
                  />
                  <div>
                    <span id="recipientEmailTooltip"><Info size={ 18 } /></span>
                    <ReactTooltip
                      className="recipientEmailTooltip"
                      anchorId="recipientEmailTooltip"
                      variant="light"
                      offset={ 10 }
                      content={ (
                        <p>{ (edit && !recipientEmail) ? t('orders.emptyAwbEmailInfo') : t('orders.awbEmailInfo') }</p>
                      ) }
                    />
                  </div>
                </div>
                { !counties
                  ? (
                    <StyledTextInputSkeleton theme={ theme }>
                      <div className="cds--form-item">
                        <span className="cds--label cds--skeleton">
                          { t('order.dropShippingData.county') }
                        </span>
                        <div className="cds--skeleton cds--text-input" />
                      </div>
                    </StyledTextInputSkeleton>
                  ) : (
                    <NWComboBox
                      titleText={ t(`pages.requestForm.${ fields.county }`) }
                      placeholder={ `${ t('common.select') } ${ t(`pages.requestForm.${ fields.county }`) }` }
                      light
                      required
                      itemToString={ item => item?.name }
                      { ...sharedComboBoxProps(fields.county) }
                      onChange={ handleSelectedCounty }
                      items={ counties || [] }
                      initialSelectedItem={
                        (counties || []).find(county => county?.erpCountyId === selectedCounty?.erpCountyId) || ''
                      }
                    />
                  )}
                { loadingCities
                  ? (
                    <StyledTextInputSkeleton theme={ theme }>
                      <div className="cds--form-item">
                        <span className="cds--label cds--skeleton">
                          { t('order.dropShippingData.city') }
                        </span>
                        <div className="cds--skeleton cds--text-input" />
                      </div>
                    </StyledTextInputSkeleton>
                  ) : (
                    <NWComboBox
                      key={ `shipping-${cities?.length}-${selectedCity?.id || 'none'}` }
                      titleText={ t(`pages.requestForm.${ fields.city }`) }
                      placeholder={ `${ t('common.select') } ${ t(`pages.requestForm.${ fields.city }`) }` }
                      light
                      required
                      itemToString={ item => item?.name }
                      disabled={ !cities }
                      { ...sharedComboBoxProps(fields.city) }
                      onChange={ ({ selectedItem }) => setValue(fields.city, selectedItem) }
                      items={ cities || [] }
                      initialSelectedItem={
                        (cities || []).find(city => city?.id === selectedCity?.id) || ''
                      }
                    />
                  )}
              </section>
              <section>
                <NWTextInput
                  light
                  required
                  labelText={ t(`pages.requestForm.${ fields.street }`) }
                  { ...sharedProps(fields.street) }
                />
                <NWTextInput
                  light
                  required
                  labelText={ t(`pages.requestForm.${ fields.number }`) }
                  { ...sharedProps(fields.number) }
                />
                <NWTextInput
                  light
                  labelText={ t(`pages.requestForm.${ fields.building }`) }
                  { ...sharedProps(fields.building) }
                />
                <div className="smallFields">
                  <NWTextInput
                    light
                    labelText={ t(`pages.requestForm.${ fields.floor }`) }
                    { ...sharedProps(fields.floor) }
                  />
                  <NWTextInput
                    light
                    labelText={ t(`pages.requestForm.${ fields.apartment }`) }
                    { ...sharedProps(fields.apartment) }
                  />
                </div>
                <NWTextInput
                  light
                  required
                  labelText={ t(`pages.requestForm.${ fields.postalCode }`) }
                  { ...sharedProps(fields.postalCode) }
                />
                <span className="deliveryLabel">{ t(`pages.requestForm.${ fields.defaultDelivery }`) }</span>
                <NWCheckbox
                  className="defaultDelivery"
                  id={ fields.defaultDelivery }
                  name={ fields.defaultDelivery }
                  invalid={ shouldShowDefaultDeliveryWarning }
                  invalidText={ t('pages.replaceDefaultDelivery') }
                  labelText={ t('pages.requestForm.defaultDeliveryLabel') }
                  { ...register(fields.defaultDelivery) }
                  // FIX: name undefined
                  onChange={ noop }
                />
              </section>
            </div>
            <div className="footer">
              <StyledButton
                onClick={ () => handleSubmit({ isNewAddress: !edit, withValidationCode: false }) }
                className="danger"
                kind="danger"
                disabled={ validationCodePending }
              >
                { (validationCodePending || isRequestPending) && <Loading active={ validationCodePending || isRequestPending } small withOverlay={ false } /> }
                { t('pages.save') }
              </StyledButton>
              <StyledButton
                kind="ghost"
                className="primary"
                onClick={ () => handleAddAddress(false) }
              >
                { t('pages.cancel') }
              </StyledButton>
            </div>
          </form>
        )}
      </StyledActions>
      <StyledAddressList>
        <div className="header">
          {/* <div className="tabs">
            <StyledButton
              kind="ghost"
              className="tab"
              onClick={ () => {} }
            >
              { t('pages.delivery') }
            </StyledButton>
          </div> */}
          <div className="search-container">
            <button
              type="button"
              onClick={ () => handleSearchAddress() }
              className="searchButton"
            />
            <Search
              className="search"
              size="lg"
              placeholder={ t('pages.searchPlaceholder') }
              labelText={ t('pages.search') }
              id="search-1"
              value={ search }
              onChange={ e => handleSearchChange(e.target.value) }
              onKeyDown={ handleSearchAddress }
              onClear={ () => handleSearchChange('') }
            />
          </div>
        </div>
        <div className="addresses-list">
          { addressesLoading && (
            ([...Array(4).keys()]).map(item => (
              <StyledSkeletonContainer key={ item } className="addresses">
                <div className="address-container">
                  <SkeletonPlaceholder className="icon-placeholder" />
                  <SkeletonText paragraph className="text-placeholder" />
                </div>
              </StyledSkeletonContainer>
            ))
          ) }
          { addresses === null && !addressesLoading && (
            <div className="noResults">{ t('pages.messages.noResults') }</div>
          )}
          { addresses.map(address => (
            <div key={ address.addressId } className="addresses">
              <div className="address-container">
                <div className="address-left">
                  <div className="address-icon-background">
                    <div className="address-icon">
                      <MapPinHouse size={ 36 } />
                    </div>
                    { (address.defaultDelivery && address.status === 2) && (
                      <div className="address-default-delivery" title="Default delivery">
                        <Check />
                      </div>
                    ) }
                  </div>
                </div>
                <div className="address-right">
                  <div className="address-data">
                    <div className="address-date">
                      <span>{ address.createdAt }</span></div>
                    <div className="address-location">{ address.countyCity }</div>
                    <div className="address-street">{ address.fullAddress }</div>
                    { address.receptionContact && (
                      <div className="reception-contact">
                        <span className="reception-contact-label">{ `${ t('pages.goodsReceptionContact') }: ` }</span>
                        <strong className="reception-contact-details">{ address.receptionContact }</strong>
                      </div>
                    ) }
                  </div>
                  <div className="address-badges">
                    { address.billing && (
                      <div className="address-default billing">{ t('pages.billingAddress') }</div>
                    ) }
                    { address.status === 0 && (
                      <div className="address-default">{ t('pages.addressPending') }</div>
                    ) }
                    { address.status === 1 && (
                      <div className="address-default">{ t('pages.addressRejected') }</div>
                    ) }
                    { address?.reason && (
                      <div className="reason">
                        <div className="reason-message">{ `${ t('pages.reason') }: ${ address.reason }` }</div>
                        { renderReason(address.reason) }
                      </div>
                    ) }
                  </div>
                </div>
                { !address.defaultBilling && (
                  <div className="address-actions">
                    <button
                      type="button"
                      className="edit"
                      onClick={ () => {
                        handleEditAddress(address.addressId);
                        actionsRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      } }
                    >
                      <Pencil />
                      <span>Editeaza</span>
                    </button>
                    <button
                      type="button"
                      className="delete"
                      onClick={ () => handleDeleteAddress(address.addressId) }
                    >
                      { (address.addressId === loadingDelete) ? <Loading active withOverlay={ false } small /> : <Trash />}
                      <span>Sterge</span>
                    </button>
                  </div>
                ) }
              </div>
            </div>
          )) }
        </div>
      </StyledAddressList>
    </StyledAddressesComponent>
  );
};

AddressesComponent.propTypes = {
  addresses: array,
  addressesLoading: bool,
  isActionsOpen: bool,
  selectedCounty: shape({
    erpCountyId: string,
    id: oneOfType([number, string]),
    name: string,
  }),
  counties: arrayOf(shape({
    erpCountyId: string,
    id: oneOfType([number, string]),
    name: string,
  })),
  handleSelectedCounty: func,
  selectedCity: shape({
    id: oneOfType([number, string]),
    name: string,
  }),
  cities: arrayOf(shape({
    id: oneOfType([number, string]),
    name: string,
  })),
  loadingCities: bool,
  handleSubmit: func,
  isRequestPending: bool,
  postError: array,
  handleSearchAddress: func,
  handleDeleteAddress: func,
  handleEditAddress: func,
  handleAddAddress: func,
  search: string,
  handleSearchChange: func,
  loadingDelete: string,
  edit: string,
  isExternalReseller: bool,
  shouldShowDefaultDeliveryWarning: bool,
  errors: array,
  email: string,
  modalOpen: bool,
  validationCodeValue: string,
  validationCodePending: bool,
  validators: object,
  register: func,
  rhfErrors: object,
  setValue: func,
  control: object,
  recipientEmail: string,
};

AddressesComponent.displayName = 'AddressesComponent';

export default AddressesComponent;
